/**
 * Centralized, provider-agnostic error reporting.
 *
 * Sentry is initialized ONLY when `VITE_SENTRY_DSN` is present at build time.
 * When it is absent the app behaves exactly as before: errors are logged to the
 * console in development and silently ignored in production.
 *
 * No secrets live in this file. See README (Error monitoring) for setup.
 */

type Scope = 'react-error-boundary' | 'unhandled-rejection' | 'window-error' | 'manual';

interface ReportContext {
  scope?: Scope;
  /** Non-sensitive tags only — never user content, tokens or payload bodies. */
  tags?: Record<string, string>;
}

type SentryLike = {
  init: (options: Record<string, unknown>) => void;
  captureException: (error: unknown, hint?: Record<string, unknown>) => void;
};

let sentry: SentryLike | null = null;
let initialized = false;

const DSN = (import.meta.env.VITE_SENTRY_DSN as string | undefined) || '';

/** Errors already handed to the reporter, so global handlers don't double-report. */
const seen = new WeakSet<object>();

const markSeen = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) return false;
  if (seen.has(error)) return true;
  seen.add(error);
  return false;
};

/** Strip anything that could carry private content before it leaves the device. */
const scrub = (event: Record<string, any>): Record<string, any> | null => {
  if (event?.request) {
    delete event.request.cookies;
    delete event.request.data;
    if (event.request.headers) delete event.request.headers;
  }
  if (event?.user) {
    // Keep an opaque id at most — never email, username or profile details.
    event.user = event.user.id ? { id: event.user.id } : undefined;
  }
  delete event.breadcrumbs;
  return event;
};

export async function initErrorReporting(): Promise<void> {
  if (initialized) return;
  initialized = true;

  if (!DSN) return;

  try {
    const mod = (await import('@sentry/react')) as unknown as SentryLike;
    mod.init({
      dsn: DSN,
      environment: import.meta.env.MODE,
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      sendDefaultPii: false,
      beforeSend: (event: Record<string, any>) => scrub(event),
    });
    sentry = mod;
  } catch (err) {
    if (import.meta.env.DEV) console.warn('[errorReporting] Sentry unavailable', err);
  }
}

export function reportError(error: unknown, context: ReportContext = {}): void {
  if (markSeen(error)) return;

  if (import.meta.env.DEV) {
    console.error(`[${context.scope || 'manual'}]`, error);
  }

  if (!sentry) return;
  try {
    sentry.captureException(error, {
      tags: { scope: context.scope || 'manual', ...(context.tags || {}) },
    });
  } catch {
    /* reporting must never break the app */
  }
}

/** Global listeners for errors React never sees. Safe to call once at boot. */
export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { scope: 'unhandled-rejection' });
  });

  window.addEventListener('error', (event) => {
    if (!event.error) return; // resource load failures carry no useful error
    reportError(event.error, { scope: 'window-error' });
  });
}

export const isErrorReportingConfigured = (): boolean => !!DSN;
